// Style editor for the sampled cgaz strip.
//
// Built dynamically because there are many small controls (overall opacity,
// geometry, shadow/border and five zone colours + alphas). Values are applied
// live through the Menu -> onHud path; `refresh()` re-reads the persisted style
// after a config apply/import.

import {
  normalizeCgazStyle,
  type CgazStyle,
  type CgazZone,
} from "../settings";
import { dsElement } from "./daisy";

export interface CgazStyleCallbacks {
  getStyle: () => CgazStyle;
  onStyle: (style: CgazStyle) => void;
}

const ZONES: { id: CgazZone; label: string }[] = [
  { id: "dead", label: "Dead" },
  { id: "partial", label: "Partial" },
  { id: "fast", label: "Fast" },
  { id: "turn", label: "Turn" },
  { id: "decel", label: "Decel" },
];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export class CgazStylePanel {
  private style: CgazStyle;
  private opacity!: HTMLInputElement;
  private opacityOut!: HTMLElement;
  private height!: HTMLInputElement;
  private heightOut!: HTMLElement;
  private width!: HTMLInputElement;
  private widthOut!: HTMLElement;
  private background!: HTMLInputElement;
  private backgroundOut!: HTMLElement;
  private shadow!: HTMLInputElement;
  private border!: HTMLInputElement;
  private borderWidth!: HTMLInputElement;
  private borderWidthOut!: HTMLElement;
  private borderColor!: HTMLInputElement;
  private borderOpacity!: HTMLInputElement;
  private borderOpacityOut!: HTMLElement;
  private zoneColor = {} as Record<CgazZone, HTMLInputElement>;
  private zoneAlpha = {} as Record<CgazZone, HTMLInputElement>;
  private zoneAlphaOut = {} as Record<CgazZone, HTMLElement>;
  private borderControls: HTMLElement[] = [];

  constructor(
    private readonly host: HTMLElement,
    private readonly cb: CgazStyleCallbacks,
  ) {
    this.style = normalizeCgazStyle(cb.getStyle());
    this.build();
    this.sync();
  }

  /** Re-read persisted values (config apply/import, reset). */
  refresh(): void {
    this.style = normalizeCgazStyle(this.cb.getStyle());
    this.sync();
  }

  private build(): void {
    this.host.innerHTML = "";
    this.host.append(
      el(
        "div",
        "weapon-note",
        "Position and size of the strip. Overall opacity multiplies the per-zone alphas; " +
          "the border is drawn around the coloured band.",
      ),
    );

    const grid = el("div", "cgaz-style-grid");
    this.host.append(grid);

    const opacity = this.rangeRow(grid, "Opacity", "hud.cgaz.opacity", 0, 1, 0.05);
    this.opacity = opacity.input;
    this.opacityOut = opacity.out;
    const height = this.rangeRow(grid, "Height (px)", "hud.cgaz.height", 2, 40, 1);
    this.height = height.input;
    this.heightOut = height.out;
    const width = this.rangeRow(grid, "Width (px)", "hud.cgaz.width", 160, 1600, 20);
    this.width = width.input;
    this.widthOut = width.out;
    const bg = this.rangeRow(grid, "Background", "hud.cgaz.bgOpacity", 0, 1, 0.05);
    this.background = bg.input;
    this.backgroundOut = bg.out;

    const shadowRow = el("div", "cgaz-style-row");
    shadowRow.append(el("label", "", "Shadow"));
    this.shadow = el("input");
    this.shadow.type = "checkbox";
    this.shadow.dataset.setting = "hud.cgaz.shadow";
    dsElement(this.shadow, "toggle", { color: "primary", size: "sm" });
    shadowRow.append(this.shadow, el("span", ""));
    grid.append(shadowRow);

    const borderRow = el("div", "cgaz-style-row");
    borderRow.append(el("label", "", "Border"));
    this.border = el("input");
    this.border.type = "checkbox";
    this.border.dataset.setting = "hud.cgaz.border";
    dsElement(this.border, "toggle", { color: "primary", size: "sm" });
    borderRow.append(this.border, el("span", ""));
    grid.append(borderRow);

    const bw = this.rangeRow(grid, "Border width", "hud.cgaz.borderWidth", 1, 6, 1);
    this.borderWidth = bw.input;
    this.borderWidthOut = bw.out;
    this.borderControls.push(bw.row);

    const bcRow = el("div", "cgaz-style-row");
    bcRow.append(el("label", "", "Border colour"));
    this.borderColor = el("input");
    this.borderColor.type = "color";
    this.borderColor.dataset.setting = "hud.cgaz.borderColor";
    bcRow.append(this.borderColor, el("span", ""));
    grid.append(bcRow);
    this.borderControls.push(bcRow);

    const bo = this.rangeRow(grid, "Border opacity", "hud.cgaz.borderOpacity", 0, 1, 0.05);
    this.borderOpacity = bo.input;
    this.borderOpacityOut = bo.out;
    this.borderControls.push(bo.row);

    this.host.append(el("div", "cgaz-zone-title", "Zone colours & alpha"));
    const zones = el("div", "cgaz-zones");
    this.host.append(zones);
    for (const zone of ZONES) {
      const row = el("div", "cgaz-zone-row");
      row.append(el("label", "", zone.label));
      const color = el("input");
      color.type = "color";
      color.title = `${zone.label} colour`;
      color.dataset.setting = `hud.cgaz.colors.${zone.id}`;
      const alpha = el("input");
      alpha.type = "range";
      alpha.min = "0";
      alpha.max = "1";
      alpha.step = "0.05";
      alpha.title = `${zone.label} alpha`;
      alpha.dataset.setting = `hud.cgaz.alphas.${zone.id}`;
      dsElement(alpha, "range", { keep: "w-full" });
      const out = el("span", "cgaz-zone-val");
      row.append(color, alpha, out);
      zones.append(row);
      this.zoneColor[zone.id] = color;
      this.zoneAlpha[zone.id] = alpha;
      this.zoneAlphaOut[zone.id] = out;
      color.addEventListener("input", () => {
        this.style.colors[zone.id] = color.value;
        this.emit();
      });
      alpha.addEventListener("input", () => {
        this.style.alphas[zone.id] = clamp(Number(alpha.value), 0, 1);
        out.textContent = Number(alpha.value).toFixed(2);
        this.emit();
      });
    }

    // Wire the simple controls once.
    this.opacity.addEventListener("input", () => {
      this.style.opacity = clamp(Number(this.opacity.value), 0, 1);
      this.opacityOut.textContent = this.style.opacity.toFixed(2);
      this.emit();
    });
    this.height.addEventListener("input", () => {
      this.style.height = clamp(Number(this.height.value), 2, 40);
      this.heightOut.textContent = `${this.style.height.toFixed(0)} px`;
      this.emit();
    });
    this.width.addEventListener("input", () => {
      this.style.width = clamp(Number(this.width.value), 160, 1600);
      this.widthOut.textContent = `${this.style.width.toFixed(0)} px`;
      this.emit();
    });
    this.background.addEventListener("input", () => {
      this.style.bgOpacity = clamp(Number(this.background.value), 0, 1);
      this.backgroundOut.textContent = this.style.bgOpacity.toFixed(2);
      this.emit();
    });
    this.shadow.addEventListener("change", () => {
      this.style.shadow = this.shadow.checked;
      this.emit();
    });
    this.border.addEventListener("change", () => {
      this.style.border = this.border.checked;
      this.syncBorder();
      this.emit();
    });
    this.borderWidth.addEventListener("input", () => {
      this.style.borderWidth = clamp(Number(this.borderWidth.value), 1, 6);
      this.borderWidthOut.textContent = `${this.style.borderWidth.toFixed(0)} px`;
      this.emit();
    });
    this.borderColor.addEventListener("input", () => {
      this.style.borderColor = this.borderColor.value;
      this.emit();
    });
    this.borderOpacity.addEventListener("input", () => {
      this.style.borderOpacity = clamp(Number(this.borderOpacity.value), 0, 1);
      this.borderOpacityOut.textContent = this.style.borderOpacity.toFixed(2);
      this.emit();
    });
  }

  private rangeRow(
    host: HTMLElement,
    label: string,
    setting: string,
    min: number,
    max: number,
    step: number,
  ): { row: HTMLElement; input: HTMLInputElement; out: HTMLElement } {
    const row = el("div", "cgaz-style-row");
    row.append(el("label", "", label));
    const input = el("input");
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.dataset.setting = setting;
    dsElement(input, "range", { keep: "w-full" });
    const out = el("span", "cgaz-style-val");
    row.append(input, out);
    host.append(row);
    return { row, input, out };
  }

  private emit(): void {
    this.cb.onStyle(normalizeCgazStyle(this.style));
  }

  private syncBorder(): void {
    for (const el of this.borderControls) {
      el.classList.toggle("cgaz-disabled", !this.style.border);
      for (const input of el.querySelectorAll("input")) {
        input.disabled = !this.style.border;
      }
    }
  }

  private sync(): void {
    const s = this.style;
    this.opacity.value = String(s.opacity);
    this.opacityOut.textContent = s.opacity.toFixed(2);
    this.height.value = String(s.height);
    this.heightOut.textContent = `${s.height.toFixed(0)} px`;
    this.width.value = String(s.width);
    this.widthOut.textContent = `${s.width.toFixed(0)} px`;
    this.background.value = String(s.bgOpacity);
    this.backgroundOut.textContent = s.bgOpacity.toFixed(2);
    this.shadow.checked = s.shadow;
    this.border.checked = s.border;
    this.borderWidth.value = String(s.borderWidth);
    this.borderWidthOut.textContent = `${s.borderWidth.toFixed(0)} px`;
    this.borderColor.value = s.borderColor;
    this.borderOpacity.value = String(s.borderOpacity);
    this.borderOpacityOut.textContent = s.borderOpacity.toFixed(2);
    for (const zone of ZONES) {
      this.zoneColor[zone.id].value = s.colors[zone.id];
      this.zoneAlpha[zone.id].value = String(s.alphas[zone.id]);
      this.zoneAlphaOut[zone.id].textContent = s.alphas[zone.id].toFixed(2);
    }
    this.syncBorder();
  }
}
